/* Common Imports */

import { styled, useTheme } from "@mui/system";
import React from "react";

/* Redux & Type Imports */

import { ObjectId } from "bson";
import { NextRouter } from "next/router";
import StateInterface from "../../../../redux-magic/state-interface";
import {
	deleteChatMessageThunk,
	fetchChatMessagesThunk,
	setChatMessagesFilterPageNumberThunk,
	setChatMessagesInitialLoadDoneThunk,
	setChatMessagesLoadingThunk,
	setChatMessagesTriggerNewSearchThunk,
	UpdateBroadcastReadStatusThunk,
	acceptedBroadcastThunk,
	removeReceivedBroadcastFromListThunk,
	setCurrentChatThunk,
	setChatReplyToThunk,
	setChatEditingThunk,
	reactToChatMessageThunk,
	sendChatMessageThunk,
} from "../../../../redux-magic/store";
import {
	ChatMessageInterface,
	ChatReplyPreview,
} from "../../../../redux-magic/sub-interfaces/chat-message-interface";
import { LastMessageInterface } from "../../../../redux-magic/sub-interfaces/chat-interface";

/* Component Imports */

import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Popover,
	Snackbar,
	Typography,
} from "@mui/material";
import { ReactionPicker, ReactionPill } from "./ChatReactions";
import { withViewerReaction } from "../../../../lib/chat/reactionEmojis";
import { useInView } from "react-intersection-observer";
import PropertyCardBroadcast from "../../../cards/PropertyCardBroadcast/PropertyCardBroadcastFetcher";
import BeegruButton from "../../../common-components/buttons/BeegruButton";
import ImageStack from "./ImageStack";
import ChatFileTiles from "./ChatFileTiles";
import { isChatAudio, isChatDocument, isChatPhoto } from "../../../../lib/chat/chatAttachmentKinds";
import ChatVoiceNotes from "./ChatVoiceNotes";
import ImageStackUploadPreview from "./ImageStackUploadPreview";
import ChatReportDialog from "./ChatReportDialog";
import { canForwardMessage, FORWARD_MAX_MESSAGES } from "./ChatForward";
import { CHAT_SEARCH_ACTIVE_ROW_TINT, highlightChatText } from "./ChatSearch";

/* Icon Imports */

import {
	AddReactionOutlined,
	Block,
	ContentCopyOutlined,
	DeleteOutline,
	FlagOutlined,
	ShortcutOutlined,
	EditOutlined,
	KeyboardArrowDown,
	KeyboardArrowDownRounded,
	ReplyOutlined,
} from "@mui/icons-material";
import ChatListingCards from "./ChatListingCards";
import { ContactCard, ListingsInterestActions, LocationCard, ReceiptGlyph, RequirementResponseCard, SharedListingsCard } from "./ChatMessageCards";
import { attachmentPreviewLabel } from "../../../../lib/chat/chatAttachmentKinds";
import { formatChatDayHeader, formatChatTime } from "../../../../lib/chatDates";

/* Constants */

/* Delete for everyone is offered on your own message for 48 hours after it was sent, WhatsApp
   style. The server enforces the same window and answers 409 past it. */

const DELETE_FOR_EVERYONE_WINDOW_MS: number = 48 * 60 * 60 * 1000;

/* Your own text message can be edited for 12 hours after it was sent (18 Oct 2026). The server
   enforces the same window (lib/chat/editMessage.ts EDIT_MESSAGE_WINDOW_MS) and answers 409 past it. */

const EDIT_MESSAGE_WINDOW_MS: number = 12 * 60 * 60 * 1000;

/* How long a finger rests on a bubble before its menu opens on a touch screen. */

const LONG_PRESS_MS: number = 500;

/* Styled Components */

/* The chat's colours, the app's (19 Oct 2026): sent bubbles peach, received white, on a near-white warm canvas. */
const SENT_BUBBLE_BG: string = "#efdac8";
const CHAT_CANVAS_BG: string = "#fff9f5";

const RootDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column-reverse",
	justifyContent: "flex-start",
	width: "100%",
	height: "100%",
	overflowY: "scroll",
	overflowX: "hidden",
	gap: "0.75rem",
	background: theme.palette.mode === "dark" ? theme.palette.background.default : CHAT_CANVAS_BG,
	padding: "0.75rem 1.25rem 1rem 1.25rem",
	position: "relative",
	overscrollBehavior: "contain",
	isolation: "isolate",
	"&::-webkit-scrollbar": {
		width: "6px",
		background: "transparent",
	},
	"&::-webkit-scrollbar-thumb": {
		background: "rgba(0, 0, 0, 0.2)",
		borderRadius: "3px",
		"&:hover": {
			background: "rgba(0, 0, 0, 0.3)",
		},
	},
	"&": {
		scrollbarWidth: "thin",
		scrollbarColor: "rgba(0, 0, 0, 0.2) transparent",
	},
	"&:not(:hover)::-webkit-scrollbar-thumb": {
		background: "transparent",
	},
	[theme.breakpoints.up("md1190")]: {
		// padding: "1.5rem 1.25rem 1rem 1.25rem",
	},
}));

const ColumnContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	gap: "0.5rem",
}));

const RowContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "flex-end",
	width: "100%",
	gap: "0.5rem",
}));

const SystemMessage = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	gap: "0.5rem",
	width: "100%",
	marginTop: "1rem",
	marginBottom: "0.5rem",
}));

const ReceivedMessage = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-end",
	alignItems: "flex-start",
	gap: "0.5rem",
	width: "100%",
}));

const SentMessage = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-end",
	alignItems: "flex-end",
	gap: "0.5rem",
	width: "100%",
}));

const ChatBubbleWrapper = styled("div")<{ isSent: boolean }>(({ theme, isSent }) => ({
	position: "relative",
	width: "fit-content",
	maxWidth: "95%",
	borderRadius: isSent ? "1rem 0rem 1rem 1rem" : "0rem 1rem 1rem 1rem",
	alignSelf: isSent ? "flex-end" : "flex-start",
	background: isSent ? SENT_BUBBLE_BG : "#ffffff",
	/* The message menu's trigger appears while the pointer is over the bubble. */
	"&:hover .message-menu-trigger": {
		opacity: 1,
	},
}));

/* The message menu's trigger: a small chevron in the bubble's top corner, shown on hover on
   a device with a pointer. A touch screen has no hover, so it opens the menu by a long press
   instead and this button stays hidden. */

const MessageMenuTrigger = styled(IconButton, {
	shouldForwardProp: (prop) => prop !== "isSent",
})<{ isSent: boolean }>(({ isSent }) => ({
	position: "absolute",
	top: "0.25rem",
	right: "0.25rem",
	zIndex: 2,
	padding: "0.125rem",
	display: "none",
	opacity: 0,
	transition: "opacity 150ms ease-in-out",
	background: isSent ? SENT_BUBBLE_BG : "#ffffff",
	"&:hover": {
		background: isSent ? SENT_BUBBLE_BG : "#ffffff",
	},
	"&:focus-visible": {
		opacity: 1,
	},
	"@media (hover: hover)": {
		display: "inline-flex",
	},
}));

/* Reply (chat audit I13): the same hover button as the menu trigger, beside it. */

const ReplyTrigger = styled(MessageMenuTrigger)({
	right: "1.75rem",
});

/* React (18 Oct 2026): the same hover button again, beside Reply. */

const ReactTrigger = styled(MessageMenuTrigger)({
	right: "3.25rem",
});

/* The reactions at the foot of a bubble, on its own side (the wrapper is the bubble's surface). */

const ReactionRow = styled("div")<{ isSent: boolean }>(({ isSent }) => ({
	display: "flex",
	justifyContent: isSent ? "flex-end" : "flex-start",
	padding: "0 0.75rem 0.5rem",
}));

/* A message's text: the body is only what was typed (chat schema 4.4). What it quotes is
   `reply_preview`, what it shares `attached_listings_details`, its files `attachments`. */

const visibleBodyOf = (body: string): string => String(body || "").trim();

/* The deleted bubble's line: a block glyph and italic grey text, WhatsApp style. */

const DeletedBody = styled(Typography)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	gap: "0.375rem",
	fontSize: "0.875rem",
	lineHeight: "1.25rem",
	fontWeight: "400",
	fontStyle: "italic",
	whiteSpace: "nowrap",
	color: theme.palette.text.secondary,
}));

/* A quoted message above a reply (19 Oct 2026, the app's look): a warm block with the brand's
   accent edge, the quoted sender's name in the accent, then their words. Above text and above a
   shared listing alike. */

const QuoteBlock = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: "0.125rem",
	width: "100%",
	minWidth: "10rem",
	padding: "0.5rem 0.75rem",
	borderRadius: "0.625rem",
	borderLeft: "3px solid " + theme.palette.primary.dark,
	background: theme.palette.background.highlight,
}));

const QuoteSender = styled(Typography)(({ theme }) => ({
	fontSize: "0.8125rem",
	lineHeight: "1.125rem",
	fontWeight: "600",
	color: theme.palette.primary.dark,
}));

const QuoteText = styled(Typography)(({ theme }) => ({
	display: "-webkit-box",
	WebkitLineClamp: 2,
	WebkitBoxOrient: "vertical",
	overflow: "hidden",
	wordBreak: "break-word",
	fontSize: "0.8125rem",
	lineHeight: "1.125rem",
	fontWeight: "400",
	color: theme.palette.text.secondary,
}));

/* Who on the viewer's own side sent a message (19 Oct 2026, `sender_person`), WhatsApp's way and the
   app's (29 Oct 2026): top-left of the bubble, at the body's size, semibold, in the brand colour.
   Never on the other side's messages. */

const SenderName = styled(Typography)(({ theme }) => ({
	alignSelf: "flex-start",
	fontSize: "0.875rem",
	lineHeight: "1.125rem",
	fontWeight: "600",
	color: theme.palette.primary.main,
	marginBottom: "0.125rem",
}));

const DeleteDialog = styled(Dialog)(() => ({
	"& .MuiDialog-paper": {
		borderRadius: "1.5rem",
		padding: "1rem 1rem 0.5rem",
		maxWidth: "26.25rem",
		width: "calc(100vw - 2rem)",
	},
}));

const DeleteDialogTitle = styled(Typography)(({ theme }) => ({
	fontWeight: 600,
	fontSize: "1.125rem",
	lineHeight: "1.5rem",
	color: theme.palette.text.primary,
}));

const DeleteDialogBody = styled(Typography)(({ theme }) => ({
	fontWeight: 400,
	fontSize: "0.875rem",
	lineHeight: "1.375rem",
	color: theme.palette.text.secondary,
}));

const DeleteDialogError = styled(Typography)(({ theme }) => ({
	fontWeight: 500,
	fontSize: "0.8125rem",
	lineHeight: "1.25rem",
	marginTop: "0.75rem",
	color: theme.palette.error.main,
}));

const ChatBubble = styled("div")<{ isSent: boolean }>(({ theme, isSent }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "flex-end",
	borderRadius: "1rem",
	gap: "0.5rem",
	padding: "1rem 1rem 1rem 1rem",
	position: "relative",
	background: isSent ? SENT_BUBBLE_BG : "#ffffff",
	"&::before": {
		content: '""',
		position: "absolute",
		width: 0,
		height: 0,
		top: "0px",
		border: 0,
		...(isSent
			? {
					right: "-8px",
					borderTop: "8px solid transparent",
					borderLeft: "8px solid " + (isSent ? SENT_BUBBLE_BG : "#ffffff"),
					borderBottom: "8px solid transparent",
					// transform: "rotate(90deg)",
				}
			: {
					left: "-8px",
					borderTop: "8px solid transparent",
					borderRight: "8px solid " + (isSent ? SENT_BUBBLE_BG : "#ffffff"),
					borderBottom: "8px solid transparent",
					// transform: "rotate(-90deg)",
				}),
		// filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
	},
}));

const ChatBubbleSystem = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	borderRadius: "1rem",
	padding: "0.5rem 1rem",
	background: theme.palette.mode === "dark" ? "rgba(145, 158, 171, 0.12)" : "rgba(99, 115, 129, 0.08)",
}));

const MessageBody = styled(Typography)(({ theme }) => ({
	fontSize: "0.875rem",
	lineHeight: "1.25rem",
	fontWeight: "400",
	width: "fit-content",
	whiteSpace: "pre-wrap",
	wordBreak: "break-word",
	"& a": {
		color: theme.palette.primary.main,
		textDecoration: "underline",
		"&:hover": {
			textDecoration: "none",
		},
	},
}));

const Time = styled(Typography)(({ theme }) => ({
	fontSize: "0.75rem",
	lineHeight: "0.875rem",
	fontWeight: "400",
	textAlign: "right",
	whiteSpace: "nowrap",
	color: theme.palette.text.secondary,
}));

/* Older messages loading: the app's three bouncing dots, centred (19 Oct 2026, the app's
   BbmLoader). A bubble-shaped placeholder guessed a side and the real messages jumped. */
const LoadingDots = styled("div")({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	gap: "0.3rem",
	"& span": {
		width: "0.4375rem",
		height: "0.4375rem",
		borderRadius: "50%",
		background: "#FC8019",
		animation: "chatLoadingDot 1.2s ease-in-out infinite",
	},
	"& span:nth-of-type(2)": { animationDelay: "0.15s" },
	"& span:nth-of-type(3)": { animationDelay: "0.3s" },
	"@keyframes chatLoadingDot": {
		"0%, 60%, 100%": { transform: "translateY(0)", opacity: 0.5 },
		"30%": { transform: "translateY(-0.3rem)", opacity: 1 },
	},
	"@media (prefers-reduced-motion: reduce)": { "& span": { animation: "none", opacity: 1 } },
});

const LoadMoreDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	height: "2.5rem",
	pointerEvents: "none",
	cursor: "none",
	background: "transparent",
}));

/* The thread and its floating "go to newest" button (19 Oct 2026): the button sits over the
   scroll area rather than in it, so it stays put while the messages scroll. Takes the thread's
   place in the screen's column. */
const ThreadFrame = styled("div")({
	position: "relative",
	display: "flex",
	flexDirection: "column",
	width: "100%",
	height: "100%",
	minHeight: 0,
});

/* How far up the history (px) the reader must be before the button shows (19 Oct 2026, the app's). */
const SCROLL_TO_NEWEST_THRESHOLD_PX: number = 160;

/* WhatsApp's round "go to newest message" button (19 Oct 2026, the app's): fades and grows in
   while the reader is up in the history. */
const ScrollToNewestButton = styled("button", {
	shouldForwardProp: (prop) => prop !== "shown",
})<{ shown: boolean }>(({ shown }) => ({
	position: "absolute",
	right: "16px",
	bottom: "12px",
	zIndex: 2,
	width: "40px",
	height: "40px",
	padding: 0,
	borderRadius: "50%",
	border: "1px solid #E9ECEF",
	background: "#FFFFFF",
	boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	cursor: "pointer",
	opacity: shown ? 1 : 0,
	transform: shown ? "scale(1)" : "scale(0.8)",
	visibility: shown ? "visible" : "hidden",
	pointerEvents: shown ? "auto" : "none",
	transition: shown
		? "opacity 180ms ease-in-out, transform 180ms ease-in-out, visibility 0s linear 0s"
		: "opacity 180ms ease-in-out, transform 180ms ease-in-out, visibility 0s linear 180ms",
	"@media (prefers-reduced-motion: reduce)": {
		transform: "none",
		transition: shown ? "opacity 150ms ease-in-out, visibility 0s linear 0s" : "opacity 150ms ease-in-out, visibility 0s linear 150ms",
	},
}));

/* The count of messages from the other side that arrived while the button showed (19 Oct 2026). */
const NewestCountBadge = styled("span")({
	position: "absolute",
	top: "-6px",
	right: "-6px",
	minWidth: "17px",
	height: "17px",
	padding: "0 4px",
	boxSizing: "border-box",
	borderRadius: "999px",
	background: "#FC8019",
	color: "#FFFFFF",
	fontSize: "11px",
	fontWeight: 700,
	lineHeight: "17px",
	textAlign: "center",
	whiteSpace: "nowrap",
});

const MessageSection = ({
	session,
	dispatch,
	router,
	realtime_websocket,
	profile_context,
	chat_id,
	messages,
	messages_count,
	messages_loading,
	initial_load_done,
	filter_page_number,
	selected_files,
	pending_uploads,
	current_message,
	setCurrentMessage,
	sendFiles,
	setSendFiles,
	setSelectedFilePreview,
	setPendingUploads,
	receiver_user_id,
	isBroadcast,
	broadcast_id,
	chat_last_message,
	has_unread_message,
	counterpart_name,
	can_react,
	reply_to_message_id,
	unread_count,
	blocked_by_me,
	forward_selection,
	on_forward_selection_change,
	counterpart_image_url,
	pending_texts,
	search_query,
	active_match_id,
	counterpart_party,
}: {
	session: StateInterface["redux_session"];
	dispatch: Function;
	router: NextRouter;
	realtime_websocket: any;
	profile_context: any;
	chat_id: string;
	messages: Array<ChatMessageInterface>;
	messages_count: number;
	messages_loading: boolean;
	initial_load_done: boolean;
	filter_page_number: number;
	selected_files: Array<string>;
	pending_uploads: Array<{ file: File; mime_type: string }>;
	current_message: string;
	setCurrentMessage: React.Dispatch<React.SetStateAction<string>>;
	sendFiles: boolean;
	setSendFiles: React.Dispatch<React.SetStateAction<boolean>>;
	setSelectedFilePreview: React.Dispatch<React.SetStateAction<Array<string>>>;
	setPendingUploads: React.Dispatch<React.SetStateAction<Array<{ file: File; mime_type: string }>>>;
	receiver_user_id: string | ObjectId;
	isBroadcast: boolean;
	broadcast_id: string | ObjectId | undefined;
	chat_last_message: LastMessageInterface | undefined;
	has_unread_message: boolean | undefined;
	/* The other side's name, for the composer's "Replying to …" (chat audit I13). */
	counterpart_name?: string;
	/* Reactions can be made: not a request, not blocked, not read-only (lib/chat/reactions.ts). */
	can_react?: boolean;
	/* The message the composer is replying to, "" when none: photos sent now quote it too. */
	reply_to_message_id?: string;
	/* How many messages this open marked read (chat/fetch-messages page 1): the unread divider. */
	unread_count?: number;
	/* This side already blocked the other: a report offers no "Also block" (18 Oct 2026). */
	blocked_by_me?: boolean;
	/* The messages picked to forward (18 Oct 2026); picking while non-empty. */
	forward_selection?: Array<string>;
	on_forward_selection_change?: (ids: Array<string>) => void;
	/* The other side's picture (the header's), beside their voice notes (19 Oct 2026). */
	counterpart_image_url?: string;
	/* Texts on their way to the server (19 Oct 2026): drawn as own bubbles with the clock until stored. */
	pending_texts?: Array<{ id: string; body: string; created_at: string }>;
	/* Search inside the conversation (gap A9): hits are marked in the text, the picked one is tinted and brought into view. */
	search_query?: string;
	active_match_id?: string;
	/* The other side, for "Also block" when reporting a broadcast card (3 Nov 2026, the app's sheet). */
	counterpart_party?: { is_business_profile: boolean; id: string } | null;
}) => {
	const theme = useTheme();

	/* The thread's own words, in the site's three languages (18 Oct 2026: they were English only). */
	const ar: boolean = router?.locale === "ar-AE";
	const kn: boolean = router?.locale === "kn-IN";
	const chatLang = {
		you: ar ? "أنت" : kn ? "ನೀವು" : "You",
		reply: ar ? "رد" : kn ? "ಉತ್ತರಿಸಿ" : "Reply",
		copy: ar ? "نسخ" : kn ? "ನಕಲಿಸಿ" : "Copy",
		copied: ar ? "تم النسخ" : kn ? "ನಕಲಿಸಲಾಗಿದೆ" : "Copied",
		copy_failed: ar ? "تعذر النسخ. يرجى المحاولة مرة أخرى." : kn ? "ನಕಲಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ." : "Couldn't copy right now. Please try again.",
		edit: ar ? "تعديل" : kn ? "ಸಂಪಾದಿಸಿ" : "Edit",
		edited: ar ? "معدّلة" : kn ? "ಸಂಪಾದಿಸಲಾಗಿದೆ" : "Edited",
		delete: ar ? "حذف" : kn ? "ಅಳಿಸಿ" : "Delete",
		options: ar ? "خيارات الرسالة" : kn ? "ಸಂದೇಶದ ಆಯ್ಕೆಗಳು" : "Message options",
		you_deleted: ar ? "لقد حذفت هذه الرسالة" : kn ? "ನೀವು ಈ ಸಂದೇಶವನ್ನು ಅಳಿಸಿದ್ದೀರಿ" : "You deleted this message",
		was_deleted: ar ? "تم حذف هذه الرسالة" : kn ? "ಈ ಸಂದೇಶವನ್ನು ಅಳಿಸಲಾಗಿದೆ" : "This message was deleted",
		not_available: ar ? "هذه الرسالة غير متاحة" : kn ? "ಈ ಸಂದೇಶ ಲಭ್ಯವಿಲ್ಲ" : "This message is not available",
		delete_title: ar ? "حذف الرسالة؟" : kn ? "ಸಂದೇಶವನ್ನು ಅಳಿಸುವುದೇ?" : "Delete message?",
		delete_everyone_body: ar
			? "الحذف لدى الجميع يترك «تم حذف هذه الرسالة» مكانها لدى الطرفين."
			: kn
				? "ಎಲ್ಲರಿಗೂ ಅಳಿಸಿದರೆ ಎರಡೂ ಕಡೆ ಅದರ ಸ್ಥಳದಲ್ಲಿ \"ಈ ಸಂದೇಶವನ್ನು ಅಳಿಸಲಾಗಿದೆ\" ಉಳಿಯುತ್ತದೆ."
				: "Deleting for everyone leaves \"This message was deleted\" in its place on both sides.",
		delete_me_body: ar
			? "ستتم إزالتها من هذه المحادثة لك فقط."
			: kn
				? "ಇದು ನಿಮಗೆ ಮಾತ್ರ ಈ ಚಾಟ್‌ನಿಂದ ತೆಗೆದುಹಾಕಲ್ಪಡುತ್ತದೆ."
				: "It will be removed from this chat for you only.",
		cancel: ar ? "إلغاء" : kn ? "ರದ್ದುಮಾಡಿ" : "Cancel",
		delete_for_me: ar ? "حذف لدي" : kn ? "ನನಗಾಗಿ ಅಳಿಸಿ" : "Delete for me",
		delete_for_everyone: ar ? "حذف لدى الجميع" : kn ? "ಎಲ್ಲರಿಗೂ ಅಳಿಸಿ" : "Delete for everyone",
		delete_too_late: ar
			? "لم يعد بالإمكان حذف هذه الرسالة لدى الجميع."
			: kn
				? "ಈ ಸಂದೇಶವನ್ನು ಇನ್ನು ಎಲ್ಲರಿಗೂ ಅಳಿಸಲು ಸಾಧ್ಯವಿಲ್ಲ."
				: "This message can no longer be deleted for everyone.",
		delete_not_yours: ar ? "يمكنك حذف رسائلك فقط." : kn ? "ನಿಮ್ಮ ಸ್ವಂತ ಸಂದೇಶಗಳನ್ನು ಮಾತ್ರ ಅಳಿಸಬಹುದು." : "You can only delete your own messages.",
		delete_failed: ar
			? "تعذر حذف الرسالة. يرجى المحاولة مرة أخرى."
			: kn
				? "ಸಂದೇಶವನ್ನು ಅಳಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ."
				: "Couldn't delete the message. Please try again.",
		react_failed: ar
			? "تعذر إضافة التفاعل. يرجى المحاولة مرة أخرى."
			: kn
				? "ಪ್ರತಿಕ್ರಿಯಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ."
				: "Couldn't react to that. Please try again.",
		broadcast: ar ? "بث" : kn ? "ಪ್ರಸಾರ" : "Broadcast",
		interested: ar ? "مهتم" : kn ? "ಆಸಕ್ತಿ ಇದೆ" : "I'm interested",
		report: ar ? "إبلاغ" : kn ? "ವರದಿ ಮಾಡಿ" : "Report",
		forward: ar ? "إعادة توجيه" : kn ? "ಫಾರ್ವರ್ಡ್ ಮಾಡಿ" : "Forward",
		report_broadcast: ar ? "إبلاغ" : kn ? "ವರದಿ ಮಾಡಿ" : "Report",
		broadcast_report_sent: ar
			? "شكرًا على الإبلاغ. لن ترى هذا البث مرة أخرى"
			: kn
				? "ವರದಿ ಮಾಡಿದ್ದಕ್ಕೆ ಧನ್ಯವಾದಗಳು. ನೀವು ಈ ಬ್ರಾಡ್‌ಕಾಸ್ಟ್ ಅನ್ನು ಮತ್ತೆ ನೋಡುವುದಿಲ್ಲ"
				: "Thanks for reporting. You won't see this broadcast again",
		report_sent: ar
			? "شكرًا على الإبلاغ. سيراجعه فريقنا."
			: kn
				? "ವರದಿ ಮಾಡಿದ್ದಕ್ಕೆ ಧನ್ಯವಾದಗಳು. ನಮ್ಮ ತಂಡ ಪರಿಶೀಲಿಸುತ್ತದೆ."
				: "Thanks for reporting. Our team will look into it",
		report_and_block_sent: ar
			? "تم الإبلاغ والحظر. لم يعد بإمكانهم مراسلتك."
			: kn
				? "ವರದಿ ಮಾಡಲಾಗಿದೆ ಮತ್ತು ನಿರ್ಬಂಧಿಸಲಾಗಿದೆ. ಅವರು ಇನ್ನು ನಿಮಗೆ ಸಂದೇಶ ಕಳುಹಿಸಲು ಸಾಧ್ಯವಿಲ್ಲ."
				: "Reported and blocked. They can no longer message you",
		not_interested: ar ? "غير مهتم" : kn ? "ಆಸಕ್ತಿ ಇಲ್ಲ" : "Not interested",
		answered_interested: ar ? "لقد أبديت اهتمامك" : kn ? "ನೀವು ಆಸಕ್ತಿ ತೋರಿಸಿದ್ದೀರಿ" : "You're interested",
		answered_not_interested: ar ? "لقد اخترت غير مهتم" : kn ? "ನೀವು ಆಸಕ್ತಿ ಇಲ್ಲ ಎಂದಿದ್ದೀರಿ" : "You said not interested",
		answer_failed: ar
			? "تعذر إرسال ردك. يرجى المحاولة مرة أخرى."
			: kn
				? "ನಿಮ್ಮ ಉತ್ತರವನ್ನು ಕಳುಹಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ."
				: "Couldn't send your answer. Please try again.",
		message: ar ? "رسالة" : kn ? "ಸಂದೇಶ" : "Message",
		// The app's divider wording (ThreadDividers.kt).
		unread: (count: number): string =>
			ar
				? count === 1
					? "رسالة واحدة غير مقروءة"
					: `${count} رسائل غير مقروءة`
				: kn
					? count === 1
						? "1 ಓದದ ಸಂದೇಶ"
						: `${count} ಓದದ ಸಂದೇಶಗಳು`
					: count === 1
						? "1 UNREAD MESSAGE"
						: `${count} UNREAD MESSAGES`,
	};

	/*

	& A broadcast from someone the viewer never chatted with (18 Oct 2026, lib/broadcasts/broadcastRequest.ts):
	& no chat exists yet. The thread is every pending broadcast from that sender (chat/fetch-broadcast-request),
	& each a card with I'm interested / Not interested, the app's layout. "I'm interested" creates the chat
	& (every card, then the reply) and the thread moves into it; "Not interested" answers that card only.

	*/

	const [requestCards, setRequestCards] = React.useState<Array<{ broadcast_id: string; invite_status: string }>>([]);
	const [answeringCard, setAnsweringCard] = React.useState<string>("");
	const [answerError, setAnswerError] = React.useState<boolean>(false);

	/* The thread becomes the real chat [chatData]: its row leaves the list, and the chat opens in place. */
	const moveIntoChat = (chatData: any) => {
		dispatch(acceptedBroadcastThunk({ broadcast_id: chat_id, chat_id: chatData._id }));
		dispatch(
			setCurrentChatThunk({
				chat_id: chatData._id,
				chat_type: chatData.chat_type || "direct",
				chat_title: chatData.chat_title || "",
				chat_description: chatData.chat_description || "",
				chat_dp: { file_id: "", exists: false, original_file_server_path: "" } as any,
				chat_is_broadcast: false,
				chat_notification_settings: {
					is_business_profile: profile_context.is_business_profile,
					user_id: profile_context.user_id,
					business_profile_id: profile_context.business_profile_id,
					is_muted: false,
					muted_till: "",
				} as any,
				chat_participants: chatData.participants || [],
				chat_created_at: chatData.created_at || new Date(),
				chat_source_collection: "chat",
				chat_last_message: chatData.last_message,
				chat_broadcast_id: undefined,
				invite_status: undefined,
			}),
		);
	};

	const loadBroadcastRequest = async () => {
		try {
			const res = await fetch(`${process.env.PRODUCTION_API_URL}chat/fetch-broadcast-request`, {
				method: "POST",
				cache: "no-store",
				body: JSON.stringify({
					broadcast_id: String(broadcast_id),
					is_business_profile: profile_context.is_business_profile,
					user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
					business_profile_id: profile_context.business_profile_id,
					sessionId: session ? (session.session_id ? session.session_id : "") : "",
				}),
			});
			const data = await res.json();
			if (data?.status !== "success") return;
			if (data.payload?.chat_id) {
				// A chat already holds this broadcast: open that chat instead.
				moveIntoChat({ _id: data.payload.chat_id, participants: [] });
				return;
			}
			setRequestCards(
				(data.payload?.messages || []).map((m: any) => ({
					broadcast_id: String(m.broadcast_id),
					invite_status: String(m.invite_status || "pending"),
				})),
			);
		} catch (error) {
			// The card the list already knows stays on screen.
		}
	};

	/* Answers one card; "I'm interested" moves the thread into the chat it creates. */
	const answerCard = (cardBroadcastId: string, status: "accepted" | "ignored") => {
		if (answeringCard) return;
		setAnsweringCard(cardBroadcastId + ":" + status);
		fetch(`${process.env.PRODUCTION_API_URL}broadcast/update-broadcast-invite-status`, {
			method: "POST",
			cache: "no-store",
			body: JSON.stringify({
				invite_status: status,
				broadcast_id: cardBroadcastId,
				responder_is_business_profile: profile_context.is_business_profile,
				responder_business_profile_id: profile_context.business_profile_id,
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
			}),
		})
			.then((res) => res.json())
			.then((data) => {
				if (data?.status !== "success") {
					setAnswerError(true);
					return;
				}
				if (status === "accepted" && data.payload?.chat?._id) {
					moveIntoChat(data.payload.chat);
					return;
				}
				const next = requestCards.map((card) =>
					card.broadcast_id === cardBroadcastId ? { ...card, invite_status: "ignored" } : card,
				);
				setRequestCards(next);
				// Nothing from this sender is pending any more: the row leaves the list.
				if (!next.some((card) => card.invite_status === "pending")) {
					dispatch(removeReceivedBroadcastFromListThunk({ broadcast_id: chat_id, invite_status: "ignored" }));
				}
			})
			.catch(() => setAnswerError(true))
			.finally(() => setAnsweringCard(""));
	};

	/* A broadcast card inside a chat that already exists (3 Nov 2026, the app's): the recipient answers it with I'm
	   interested / Not interested (after Not interested, I'm interested stays offered); the sender's own card reads
	   "Sent to N contacts · N responses". The status and counts come with the thread (fetchMessagesCore). */
	const [cardAnswers, setCardAnswers] = React.useState<Record<string, string>>({});
	const [answeringChatCard, setAnsweringChatCard] = React.useState<string>("");
	const answerChatCard = (cardBroadcastId: string, status: "accepted" | "ignored") => {
		if (answeringChatCard) return;
		setAnsweringChatCard(cardBroadcastId + ":" + status);
		fetch(`${process.env.PRODUCTION_API_URL}broadcast/update-broadcast-invite-status`, {
			method: "POST",
			cache: "no-store",
			body: JSON.stringify({
				invite_status: status,
				broadcast_id: cardBroadcastId,
				responder_is_business_profile: profile_context.is_business_profile,
				responder_business_profile_id: profile_context.business_profile_id,
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
			}),
		})
			.then((res) => res.json())
			.then((data) => {
				if (data?.status === "success") setCardAnswers((current) => ({ ...current, [cardBroadcastId]: status }));
				else setAnswerError(true);
			})
			.catch(() => setAnswerError(true))
			.finally(() => setAnsweringChatCard(""));
	};
	const renderBroadcastCardFooter = (message: any): React.ReactNode => {
		const details: any = message.broadcast_details;
		const cardBroadcastId: string = message.broadcast_id ? String(message.broadcast_id) : "";
		if (!details || !cardBroadcastId) return null;
		if (details.is_own_broadcast) {
			const sent: number = Number(details.recipients_count) || 0;
			const responses: number = Number(details.responses_count) || 0;
			return (
				<Typography variant="caption" sx={{ color: "text.secondary", marginTop: "0.25rem" }}>
					{`Sent to ${sent} ${sent === 1 ? "contact" : "contacts"} · ${responses} ${responses === 1 ? "response" : "responses"}`}
				</Typography>
			);
		}
		const status: string = cardAnswers[cardBroadcastId] || String(message.invite_status || details.invite_status || "");
		if (status !== "pending" && status !== "ignored") return null;
		return (
			<div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", width: "100%", marginTop: "0.5rem" }}>
				<div style={{ display: "flex", flexDirection: "row", gap: "0.5rem", width: "100%" }}>
					{status === "pending" ? (
						<BeegruButton
							variant="outlined"
							flavor="primary"
							loading={answeringChatCard === cardBroadcastId + ":ignored"}
							disabled={Boolean(answeringChatCard)}
							onClick={() => answerChatCard(cardBroadcastId, "ignored")}
							sx={{ flex: 1, textTransform: "none", fontWeight: 600 }}
						>
							{chatLang.not_interested}
						</BeegruButton>
					) : null}
					<BeegruButton
						variant="contained"
						flavor="primary"
						loading={answeringChatCard === cardBroadcastId + ":accepted"}
						disabled={Boolean(answeringChatCard)}
						onClick={() => answerChatCard(cardBroadcastId, "accepted")}
						sx={{ flex: 1, textTransform: "none", fontWeight: 600 }}
					>
						{chatLang.interested}
					</BeegruButton>
				</div>
				{status === "ignored" ? (
					<Typography variant="caption" sx={{ color: "text.secondary" }}>
						{chatLang.answered_not_interested}
					</Typography>
				) : null}
			</div>
		);
	};

	/* Fetch messages on initial load */
	React.useEffect(() => {
		if (!initial_load_done && session) {
			dispatch(setChatMessagesTriggerNewSearchThunk(true));
			dispatch(setChatMessagesInitialLoadDoneThunk(true));
			dispatch(setChatMessagesLoadingThunk(true));
			if (!isBroadcast) {
				dispatch(
					fetchChatMessagesThunk({
						chat_id: chat_id,
						is_business_profile: profile_context.is_business_profile,
						user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
						business_profile_id: profile_context.business_profile_id,
						page: 1,
						sessionId: session ? (session.session_id ? session.session_id : "") : "",
					}),
				);
			}
			else if (isBroadcast && broadcast_id) {
				// Every pending broadcast from this sender, each its own card (18 Oct 2026); opening it
				// marks them read. When a chat already holds the card, the thread moves into it.
				loadBroadcastRequest();
				if (has_unread_message) {
					dispatch(
						UpdateBroadcastReadStatusThunk({
							_id: chat_id,
							is_business_profile: profile_context.is_business_profile,
							user_id: profile_context.user_id,
							business_profile_id: profile_context.is_business_profile ? profile_context.business_profile_id : undefined,
						}),
					);
				}
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initial_load_done, session]);

	/* Load more messages on scroll (Infinite scroll) */

	const { ref, inView } = useInView({ threshold: 0 });

	React.useEffect(() => {
		if (inView && initial_load_done && session) {
			dispatch(setChatMessagesFilterPageNumberThunk(filter_page_number + 1));
			dispatch(setChatMessagesLoadingThunk(true));
			if(!isBroadcast){
				dispatch(
					fetchChatMessagesThunk({
						chat_id: chat_id,
						is_business_profile: profile_context.is_business_profile,
						user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
						business_profile_id: profile_context.business_profile_id,
						page: filter_page_number ? filter_page_number + 1 : 1,
						sessionId: session ? (session.session_id ? session.session_id : "") : "",
					}),
				);
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inView]);

	// Add this helper function at the top of the file with other imports
	const convertLinksToElements = (text: string, highlight: string = "") => {
		const urlPattern = /((https?:\/\/)?[\w-]+(\.[\w-]+)+\.?(:\d+)?(\/\S*)?)/gi;
		const emailPattern =
			/([a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.(com|org|net|edu|gov|mil|info|io|ai|ae|in|uk|us|ca|au|de|fr|jp|cn|ru|br|it|nl|es|se|no|fi|dk|ch|at|be|ie|nz))\b/gi;

		/* A search marks its hits in the plain text between links (gap A9); runs of words are joined first so
		   a phrase across spaces is marked whole. */
		if (highlight.trim()) {
			const out: Array<React.ReactNode> = [];
			let plain: string = "";
			const flush = (index: number) => {
				if (plain) out.push(<React.Fragment key={"t" + index}>{highlightChatText(plain, highlight, "h" + index)}</React.Fragment>);
				plain = "";
			};
			text.split(/(\s+)/).forEach((part, index) => {
				if (part.trim() && (part.match(emailPattern) || part.match(urlPattern))) {
					flush(index);
					out.push(convertLinksToElements(part)[0]);
				} else plain += part;
			});
			flush(-1);
			return out;
		}

		return text.split(/(\s+)/).map((part, index) => {
			if (!part.trim()) return part; // Return spaces and newlines as-is

			if (part.match(emailPattern)) {
				return (
					<a
						key={index}
						href={`mailto:${part}`}
						style={{
							color: "#1976d2",
							textDecoration: "underline",
							wordBreak: "break-all",
						}}
					>
						{part}
					</a>
				);
			}

			if (part.match(urlPattern)) {
				const url = part.startsWith("http") ? part : `https://${part}`;
				return (
					<a
						key={index}
						href={url}
						target="_blank"
						rel="noopener noreferrer"
						style={{
							color: "#1976d2",
							textDecoration: "underline",
							wordBreak: "break-all",
						}}
					>
						{part}
					</a>
				);
			}

			return part;
		});
	};

	/* Function to handle the click event on a message */

	/* The day header, in the app's style (lib/chatDates.ts, chat audit I12): TODAY, YESTERDAY, else
	   26 AUG 2026. It used to be a US long date. */
	const getMessageDate = (date: Date): string =>
		formatChatDayHeader(date, router?.locale, {
			today: router?.locale === "ar-AE" ? "اليوم" : router?.locale === "kn-IN" ? "ಇಂದು" : "Today",
			yesterday: router?.locale === "ar-AE" ? "أمس" : router?.locale === "kn-IN" ? "ನಿನ್ನೆ" : "Yesterday",
		});

	/* A message's time, as every bubble shows it. */

	// The app's time format, 12 or 24-hour by the browser's setting (it was always US 12-hour).
	const formatMessageTime = (createdAt: Date | string): string => formatChatTime(createdAt);

	/* --- Whose message is it --- */

	/* An id that may arrive as a string, an ObjectId or a populated document. */

	const idOf = (value: any): string =>
		value === null || value === undefined
			? ""
			: typeof value === "object" && value._id !== undefined
				? String(value._id)
				: String(value);

	const sessionUserId: string = session && session.user && session.user.dbUser ? idOf(session.user.dbUser._id) : "";

	/* The picture on the viewer's own voice notes (19 Oct 2026): the acting profile's, so the
	   business logo while acting as a business, the person's own photo otherwise. Read from the
	   session, so a note just sent shows it at once. */
	const ownAvatarUrl: string =
		profile_context && profile_context.is_business_profile === true
			? profile_context.business_profile_image?.exists
				? String(profile_context.business_profile_image.file_id || "")
				: ""
			: session?.user?.dbUser?.profile_picture?.exists
				? String(session.user.dbUser.profile_picture.file_id || "")
				: "";
	const ownAvatarName: string =
		profile_context && profile_context.is_business_profile === true
			? String(profile_context.page_title || "")
			: `${session?.user?.dbUser?.first_name || ""} ${session?.user?.dbUser?.last_name || ""}`.trim();

	/* A message is "own" when its sender is the acting profile, decided by party: acting as a
	   business, the sender is that business; acting personally, the sender is the signed-in
	   person. Drives the bubble's side, its deleted wording and Delete for everyone. */

	const isOwnMessage = (message: any): boolean => {
		if (!message || !message.sender) return false;
		if (profile_context && profile_context.is_business_profile === true) {
			return (
				message.sender.is_business_profile === true &&
				idOf(message.sender.business_profile_id) !== "" &&
				idOf(message.sender.business_profile_id) === idOf(profile_context.business_profile_id)
			);
		}
		return (
			message.sender.is_business_profile !== true &&
			sessionUserId !== "" &&
			idOf(message.sender.user_id) === sessionUserId
		);
	};

	/* A message deleted for everyone arrives with `is_deleted: true` (chat schema 4.6). */

	const isDeletedMessage = (message: any): boolean => message.is_deleted === true;

	const canDeleteForEveryone = (message: any): boolean =>
		isOwnMessage(message) &&
		!isDeletedMessage(message) &&
		Date.now() - new Date(message.created_at).getTime() < DELETE_FOR_EVERYONE_WINDOW_MS;

	/* --- The unread divider (18 Oct 2026, the app's) ---
	   Over the oldest of the [unread_count] newest received messages (messages are newest first),
	   placed once per open: a message arriving after it doesn't move it. */

	const unreadDividerId: string = React.useMemo(() => {
		const count: number = Number(unread_count || 0);
		if (count <= 0) return "";
		let remaining: number = count;
		let id: string = "";
		for (const message of messages as Array<any>) {
			if (message.message_type === "system" || isOwnMessage(message)) continue;
			id = idOf(message._id);
			remaining -= 1;
			if (remaining <= 0) break;
		}
		return id;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chat_id, unread_count]);

	/* --- Go to the newest message (19 Oct 2026, WhatsApp's, the same as the app) ---
	   The thread runs bottom up (column-reverse), so scrollTop is 0 at the newest message and grows
	   (negative in most browsers) as the reader scrolls up: Math.abs covers both conventions. The
	   badge counts the other side's messages that arrived while the reader was away. */

	const threadRef = React.useRef<HTMLDivElement | null>(null);
	const [awayFromNewest, setAwayFromNewest] = React.useState<boolean>(false);
	const awayFromNewestRef = React.useRef<boolean>(false);
	const [newWhileAway, setNewWhileAway] = React.useState<number>(0);
	/* The newest message id already accounted for. */
	const seenNewestIdRef = React.useRef<string>("");

	const onThreadScroll = () => {
		const thread: HTMLDivElement | null = threadRef.current;
		if (!thread) return;
		const away: boolean = Math.abs(thread.scrollTop) > SCROLL_TO_NEWEST_THRESHOLD_PX;
		if (away === awayFromNewestRef.current) return;
		awayFromNewestRef.current = away;
		setAwayFromNewest(away);
		if (!away) {
			seenNewestIdRef.current = messages.length > 0 ? idOf((messages[0] as any)._id) : "";
			setNewWhileAway(0);
		}
	};

	/* A new chat starts at its newest message with nothing counted. */
	React.useEffect(() => {
		awayFromNewestRef.current = false;
		setAwayFromNewest(false);
		setNewWhileAway(0);
		seenNewestIdRef.current = "";
	}, [chat_id]);

	/* Count the other side's messages newer than the last one accounted for, while away. */
	React.useEffect(() => {
		const newestId: string = messages.length > 0 ? idOf((messages[0] as any)._id) : "";
		const previousId: string = seenNewestIdRef.current;
		seenNewestIdRef.current = newestId;
		if (!awayFromNewestRef.current || previousId === "" || newestId === "" || newestId === previousId) return;
		let incoming: number = 0;
		let found: boolean = false;
		for (const message of messages as Array<any>) {
			if (idOf(message._id) === previousId) {
				found = true;
				break;
			}
			if (message.message_type !== "system" && !isOwnMessage(message)) incoming += 1;
		}
		if (found && incoming > 0) setNewWhileAway((count: number) => count + incoming);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [messages]);

	const scrollToNewest = () => {
		const thread: HTMLDivElement | null = threadRef.current;
		if (!thread) return;
		const reduceMotion: boolean =
			typeof window !== "undefined" &&
			typeof window.matchMedia === "function" &&
			window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		thread.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
	};

	const showScrollToNewest: boolean = awayFromNewest && initial_load_done;

	/* --- The delete dialog (16 Oct 2026, WhatsApp's way, same as the app) ---
	   The options menu's Delete opens one dialog holding the choices: "Delete for everyone" (own
	   message, within 48 hours), "Delete for me" and Cancel, so one click decides. */

	/* The message the dialog is for. Kept while the dialog fades out (cleared on exit) so its
	   choices don't change mid-fade. */

	const [deleteTarget, setDeleteTarget] = React.useState<any | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = React.useState<boolean>(false);
	/* Which delete is in flight, for its button's spinner; null while none is. */
	const [deleting, setDeleting] = React.useState<"everyone" | "me" | null>(null);
	const [deleteError, setDeleteError] = React.useState<string>("");

	const openDeleteDialog = (message: any) => {
		setDeleteTarget(message);
		setDeleteError("");
		setDeleteDialogOpen(true);
	};

	const closeDeleteDialog = () => {
		if (deleting) return;
		setDeleteDialogOpen(false);
	};

	const confirmDelete = async (deleteFor: "everyone" | "me") => {
		if (!deleteTarget || deleting) return;
		setDeleting(deleteFor);
		setDeleteError("");
		const result = await dispatch(
			deleteChatMessageThunk({
				delete_for: deleteFor,
				chat_id: idOf(deleteTarget.chat_id) || chat_id,
				message_id: idOf(deleteTarget._id),
				is_business_profile: profile_context.is_business_profile === true,
				user_id: sessionUserId,
				business_profile_id: profile_context.business_profile_id,
			}),
		);
		setDeleting(null);
		if (result && result.ok) {
			setDeleteDialogOpen(false);
			return;
		}

		/* Our own short wording, never the server's text. */

		setDeleteError(
			result && result.http_status === 409
				? chatLang.delete_too_late
				: result && result.http_status === 403
					? chatLang.delete_not_yours
					: chatLang.delete_failed,
		);
	};

	/* --- The message options (18 Oct 2026, the app's) ---
	   The bubble's arrow, or a long press on a touch screen, opens one menu: the reactions (when the
	   message can take one), Reply, Copy, Edit (your own text, within 12 hours) and Delete, which
	   opens the delete choices above. */

	const [optionsAnchor, setOptionsAnchor] = React.useState<HTMLElement | null>(null);
	const [optionsTarget, setOptionsTarget] = React.useState<any | null>(null);
	/* Reporting a message the other side sent (18 Oct 2026, ChatReportDialog): the message, null when closed. */
	const [reportTarget, setReportTarget] = React.useState<any | null>(null);
	const [reportNotice, setReportNotice] = React.useState<string>("");
	/* A broadcast card reported on its own (3 Nov 2026, the app's "Report this broadcast"): it is declined for this
	   side, so it leaves the thread. */
	const [reportBroadcastId, setReportBroadcastId] = React.useState<string>("");
	const [reportedBroadcastIds, setReportedBroadcastIds] = React.useState<Array<string>>([]);
	const [copyNotice, setCopyNotice] = React.useState<string>("");

	const openOptions = (anchor: HTMLElement, message: any) => {
		setOptionsTarget(message);
		setOptionsAnchor(anchor);
	};

	const closeOptions = () => setOptionsAnchor(null);

	/* What Copy puts on the clipboard: the message's text, or nothing to copy. */
	const copyTextOf = (message: any): string => (isDeletedMessage(message) ? "" : visibleBodyOf(message?.body || "").trim());

	/* Your own text message (not a caption, card, location or contact), sent by you, within the window. */
	const canEdit = (message: any): boolean =>
		can_react === true &&
		isOwnMessage(message) &&
		!isDeletedMessage(message) &&
		message?.message_type === "text" &&
		idOf(message?.sender?.user_id) === sessionUserId &&
		Date.now() - new Date(message.created_at).getTime() < EDIT_MESSAGE_WINDOW_MS;

	const copyMessage = (message: any) => {
		const text: string = copyTextOf(message);
		closeOptions();
		if (!text) return;
		const clipboard = typeof navigator !== "undefined" ? navigator.clipboard : undefined;
		if (!clipboard) {
			setCopyNotice(chatLang.copy_failed);
			return;
		}
		clipboard
			.writeText(text)
			.then(() => setCopyNotice(chatLang.copied))
			.catch(() => setCopyNotice(chatLang.copy_failed));
	};

	const startEdit = (message: any) => {
		closeOptions();
		if (!canEdit(message)) return;
		dispatch(setChatEditingThunk({ message_id: idOf(message._id), body: visibleBodyOf(message.body || "") }));
	};

	/* --- Reactions (18 Oct 2026, lib/chat/reactions.ts) ---
	   One per person, from six emojis: the hover button or the pill opens the picker, and a long
	   press on a touch screen shows it at the top of the delete dialog, as in the app. Shown at once,
	   settled on the server's answer, put back on failure. */

	const [pickerAnchor, setPickerAnchor] = React.useState<HTMLElement | null>(null);
	const [pickerTarget, setPickerTarget] = React.useState<any | null>(null);
	const [reactionError, setReactionError] = React.useState<string>("");

	const canReactTo = (message: any): boolean =>
		can_react === true && !!message?._id && !isDeletedMessage(message) && message.message_type !== "system";

	const openPicker = (event: React.MouseEvent<HTMLElement>, message: any) => {
		setPickerTarget(message);
		setPickerAnchor(event.currentTarget);
	};

	const react = async (message: any, picked: string) => {
		setPickerAnchor(null);
		closeOptions();
		if (!canReactTo(message)) return;
		const previous: Array<any> = message.reaction_summary || [];
		const current: string | undefined = previous.find((line: any) => line.by_viewer)?.emoji;
		const emoji: string = current === picked ? "" : picked;
		const result = await dispatch(
			reactToChatMessageThunk({
				chat_id: idOf(message.chat_id) || chat_id,
				message_id: idOf(message._id),
				emoji,
				is_business_profile: profile_context.is_business_profile === true,
				business_profile_id: profile_context.business_profile_id,
				optimistic: withViewerReaction(previous, emoji),
				previous,
			}),
		);
		if (result && !result.ok) setReactionError(result.error || chatLang.react_failed);
	};

	/* --- Long press and swipe to reply (touch screens) --- */

	const longPressTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
	const longPressFired = React.useRef<boolean>(false);
	const touchActive = React.useRef<boolean>(false);

	/* Swipe right to reply (18 Oct 2026, the app's): the bubble follows the finger at 60% (up to
	   76px); let go past 56px and the reply starts, then it eases home. A vertical drag still
	   scrolls the thread. Only where the thread can be answered (the same rule as reacting). */
	const SWIPE_REPLY_THRESHOLD_PX: number = 56;
	const SWIPE_REPLY_MAX_PX: number = 76;
	const touchStart = React.useRef<{ x: number; y: number } | null>(null);
	const [swipe, setSwipe] = React.useState<{ id: string; dx: number } | null>(null);

	const cancelLongPress = () => {
		if (longPressTimer.current) {
			clearTimeout(longPressTimer.current);
			longPressTimer.current = null;
		}
	};

	/* A pending long press must not fire after the thread has gone. */

	React.useEffect(() => {
		return () => cancelLongPress();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const longPressHandlers = (message: any) => ({
		onTouchStart: (event: React.TouchEvent<HTMLDivElement>) => {
			touchActive.current = true;
			longPressFired.current = false;
			cancelLongPress();
			const touch = event.touches[0];
			touchStart.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
			const bubble: HTMLElement = event.currentTarget;
			longPressTimer.current = setTimeout(() => {
				longPressTimer.current = null;
				longPressFired.current = true;
				openOptions(bubble, message);
			}, LONG_PRESS_MS);
		},
		onTouchMove: (event: React.TouchEvent<HTMLDivElement>) => {
			cancelLongPress();
			const start = touchStart.current;
			const touch = event.touches[0];
			if (!start || !touch || !canReactTo(message)) return;
			const dx: number = touch.clientX - start.x;
			const dy: number = Math.abs(touch.clientY - start.y);
			// Only a rightward drag that is more sideways than up or down.
			if (dx > 8 && dx > dy) {
				setSwipe({ id: idOf(message._id), dx: Math.min(dx * 0.6, SWIPE_REPLY_MAX_PX) });
			}
		},
		onTouchEnd: (event: React.TouchEvent<HTMLDivElement>) => {
			cancelLongPress();
			touchActive.current = false;
			touchStart.current = null;
			if (swipe && swipe.id === idOf(message._id)) {
				if (swipe.dx >= SWIPE_REPLY_THRESHOLD_PX) startReply(message, isOwnMessage(message));
				setSwipe(null);
			}
			/* The lift after a long press must not also tap what is under the finger (a link,
			   a photo, or the menu's own backdrop, which would close it at once). */
			if (longPressFired.current) {
				event.preventDefault();
				longPressFired.current = false;
			}
		},
		onTouchCancel: () => {
			cancelLongPress();
			touchActive.current = false;
			touchStart.current = null;
			setSwipe(null);
		},
		onContextMenu: (event: React.MouseEvent<HTMLDivElement>) => {
			/* A held finger raises the browser's own context menu; the message menu replaces it. */
			if (touchActive.current || longPressFired.current) event.preventDefault();
		},
	});

	/* --- Bubble content --- */

	/* The quote above a reply. A quoted message deleted for everyone reads "This message was deleted". */

	const renderQuote = (preview: ChatReplyPreview): JSX.Element => {
		const quotedText: string = preview.body
			? preview.body
			: preview.is_broadcast && preview.property_title
				? preview.property_title
				: chatLang.not_available;
		return (
			<QuoteBlock>
				{!preview.is_deleted && (preview.sender_is_viewer || preview.sender_name) ? (
					<QuoteSender variant="body2">{preview.sender_is_viewer ? chatLang.you : preview.sender_name}</QuoteSender>
				) : null}
				{preview.is_deleted ? (
					<DeletedBody
						variant="body2"
						sx={{ fontSize: "0.8125rem" }}
					>
						<Block sx={{ fontSize: "0.875rem" }} />
						{chatLang.was_deleted}
					</DeletedBody>
				) : (
					<QuoteText variant="body2">{quotedText}</QuoteText>
				)}
			</QuoteBlock>
		);
	};

	/* One bubble, sent or received. A message deleted for everyone keeps its place, side and
	   time but shows only the deleted line: no text, files, cards, links or quote. */

	/* Reply to [message] (chat audit I13): the composer shows the quote and sends `reply_to`. The
	   quote's line is the message's text, else what it holds (a photo, a voice note, a listing). */
	const startReply = (message: any, own: boolean) => {
		const text: string = visibleBodyOf(message.body);
		const body: string =
			text ||
			attachmentPreviewLabel(message.attachments || []) ||
			(message.broadcast_details?.title ? String(message.broadcast_details.title) : "") ||
			(Array.isArray(message.attached_listings_details) && message.attached_listings_details[0]?.title
				? String(message.attached_listings_details[0].title)
				: "") ||
			(message.is_broadcast ? chatLang.broadcast : chatLang.message);
		dispatch(
			setChatReplyToThunk({
				message_id: String(message._id),
				sender_name: own ? chatLang.you : counterpart_name || "",
				body: body.length > 140 ? `${body.slice(0, 140)}…` : body,
			}),
		);
	};

	/* Picking messages to forward (18 Oct 2026): a click on a message picks or unpicks it, over
	   whatever it would do; the last one unpicked ends picking. */
	const picking: boolean = (forward_selection || []).length > 0;
	const toggleForward = (message: any) => {
		if (!on_forward_selection_change || !canForwardMessage(message)) return;
		const id: string = String(message._id);
		const current: Array<string> = forward_selection || [];
		if (current.includes(id)) on_forward_selection_change(current.filter((x) => x !== id));
		else if (current.length < FORWARD_MAX_MESSAGES) on_forward_selection_change([...current, id]);
	};

	/* "I'm Interested" / "Not interested" on the other side's listings card (gap A7, the app's RequirementResponse CTAs):
	   the cards answered here, so their buttons go at once; the server's `cta_action` keeps them gone after a reload. */
	const [answeredCards, setAnsweredCards] = React.useState<Array<string>>([]);
	const recordCardAction = async (message: any, action: "responded" | "ignored"): Promise<boolean> => {
		try {
			const res = await fetch(process.env.PRODUCTION_API_URL + "chat/update-message-action", {
				method: "POST",
				cache: "no-store",
				headers: { "Content-Type": "text/plain" },
				body: JSON.stringify({
					message_id: String(message._id),
					chat_id: String(chat_id),
					action,
					is_business_profile: profile_context?.is_business_profile === true,
					business_profile_id: profile_context?.is_business_profile ? profile_context.business_profile_id : undefined,
				}),
			});
			const data = await res.json();
			return data?.status === "success";
		} catch {
			return false;
		}
	};
	const respondToCard = async (message: any, propertyIds: Array<string>): Promise<boolean> => {
		const outcome = (await dispatch(
			sendChatMessageThunk({
				chat_id: chat_id,
				message_id: new ObjectId().toString(),
				sender_is_business_profile: profile_context.is_business_profile,
				sender_user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
				sender_business_profile_id: profile_context.business_profile_id,
				message_body: "I'm interested in these properties",
				attachments: [],
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
				shared_entity: propertyIds.map((id) => ({ entity_type: "property" as const, entity_id: id })),
			}),
		)) as { ok: boolean } | undefined;
		if (!outcome?.ok) return false;
		// The reply is out; the decision is recorded so the buttons stay gone (best effort, as in the app).
		await recordCardAction(message, "responded");
		setAnsweredCards((current) => [...current, String(message._id)]);
		return true;
	};
	const ignoreCard = async (message: any): Promise<boolean> => {
		const ok = await recordCardAction(message, "ignored");
		if (ok) setAnsweredCards((current) => [...current, String(message._id)]);
		return ok;
	};

	/* The picked search hit's row is tinted (gap A9). */
	const searchRowStyle = (message: any): React.CSSProperties | undefined =>
		active_match_id && idOf(message._id) === active_match_id
			? { backgroundColor: CHAT_SEARCH_ACTIVE_ROW_TINT, borderRadius: "0.75rem", transition: "background-color 200ms" }
			: undefined;

	/* ...and brought to the middle of the thread whenever the pick changes. */
	React.useEffect(() => {
		if (!active_match_id || !threadRef.current) return;
		const row = threadRef.current.querySelector(`[data-chat-message-id="${CSS.escape(active_match_id)}"]`);
		if (row) (row as HTMLElement).scrollIntoView({ block: "center", behavior: "smooth" });
	}, [active_match_id]);

	const renderBubble = (message: any, own: boolean): JSX.Element => {
		const deleted: boolean = isDeletedMessage(message);
		const attachments: Array<any> = deleted ? [] : message.attachments || [];
		const pickedForForward: boolean = picking && (forward_selection || []).includes(String(message._id));
		/* The cards the server resolved for this message (lib/chat/messageCards.ts), none on a tombstone. */
		const locationDetails: any = !deleted && message.message_type === "location" ? message.location_details || null : null;
		const contactDetails: any = !deleted && message.message_type === "contact" ? message.contact_details || null : null;
		const requirementDetails: any = !deleted ? message.requirement_details || null : null;
		const listings: Array<any> =
			!deleted && Array.isArray(message.attached_listings_details) ? message.attached_listings_details.filter(Boolean) : [];
		const bodyText: string = visibleBodyOf(message.body);
		/* The app's actionable card: the other side's requirement response or share of two or more listings, unanswered. */
		const cardActions: React.ReactNode =
			!own &&
			!deleted &&
			listings.length > 0 &&
			(requirementDetails || listings.length > 1) &&
			!message.cta_action &&
			!answeredCards.includes(String(message._id)) ? (
				<ListingsInterestActions
					router={router}
					listings={listings}
					onRespond={(ids) => respondToCard(message, ids)}
					onIgnore={() => ignoreCard(message)}
				/>
			) : null;
		/* A location, a contact card or a requirement response draws its words inside the card. */
		const bodyInCard: boolean = Boolean(locationDetails || contactDetails || requirementDetails);
		return (
			<ChatBubbleWrapper
				isSent={own}
				onClickCapture={
					picking
						? (event: React.MouseEvent) => {
								event.preventDefault();
								event.stopPropagation();
								toggleForward(message);
							}
						: undefined
				}
				sx={{
					// Picked to forward: an orange ring around the bubble.
					outline: pickedForForward ? `2px solid ${theme.palette.primary.main}` : "none",
					outlineOffset: "2px",
					borderRadius: "1rem",
					cursor: picking ? (canForwardMessage(message) ? "pointer" : "not-allowed") : undefined,
					minWidth: message.is_broadcast && !deleted ? "95%" : "fit-content",
					// Swipe to reply: follows the finger, eases home on release.
					transform: swipe && swipe.id === idOf(message._id) ? `translateX(${swipe.dx}px)` : "translateX(0px)",
					transition: swipe && swipe.id === idOf(message._id) ? "none" : "transform 250ms cubic-bezier(.32,.72,0,1)",
				}}
				{...longPressHandlers(message)}
			>
				<ChatBubble isSent={own}>
					<ColumnContainer>
						{deleted ? (
							<RowContainer>
								<DeletedBody variant="body2">
									<Block sx={{ fontSize: "1rem" }} />
									{own ? chatLang.you_deleted : chatLang.was_deleted}
								</DeletedBody>
								<Time variant="body2">{formatMessageTime(message.created_at)}</Time>
							</RowContainer>
						) : (
							<React.Fragment>
								{own && message.sender_person?.name ? (
									<SenderName variant="body2">{String(message.sender_person.name)}</SenderName>
								) : null}
								{message.is_broadcast ? (
									<PropertyCardBroadcast
										session={session}
										dispatch={dispatch}
										profile_context={profile_context}
										router={router}
										broadcast_id={message.broadcast_id}
										source="chat_message"
									/>
								) : null}
								{message.is_broadcast ? renderBroadcastCardFooter(message) : null}
								{message.reply_preview ? renderQuote(message.reply_preview) : null}
								{/* Photos as the image stack, documents (a PDF) as file cards, voice notes as players
								    after the sender's picture (their teammate's on the viewer's side). */}
								{attachments.some((att: any) => isChatPhoto(att)) && (
									<ImageStack
										images={attachments.filter((att: any) => isChatPhoto(att)).map((att: any) => att.file_id)}
									/>
								)}
								{attachments.some((att: any) => isChatDocument(att)) && (
									<ChatFileTiles files={attachments.filter((att: any) => isChatDocument(att))} />
								)}
								{attachments.some((att: any) => isChatAudio(att)) && (
									<ChatVoiceNotes
										notes={attachments.filter((att: any) => isChatAudio(att))}
										sender_image_url={own ? ownAvatarUrl : counterpart_image_url || ""}
										sender_name={own ? ownAvatarName : counterpart_name || ""}
									/>
								)}
								{/* A shared place and a business's contact card (lib/chat/messageCards.ts); the body
								    the server wrote for them is never shown as text once the card is there. */}
								{locationDetails ? <LocationCard details={locationDetails} /> : null}
								{contactDetails ? (
									<ContactCard
										details={contactDetails}
										locale={router?.locale}
									/>
								) : null}
								{/* A requirement response carries its words and listings inside its own card; any
								    other share is one listing card, or a group card for two or more. */}
								{requirementDetails ? (
									<RequirementResponseCard
										router={router}
										details={requirementDetails}
										listings={listings}
										actions={cardActions}
										body={
											bodyText ? (
												<MessageBody variant="body2">{convertLinksToElements(bodyText, search_query || "")}</MessageBody>
											) : null
										}
									/>
								) : listings.length > 1 ? (
									<SharedListingsCard
										router={router}
										listings={listings}
										actions={cardActions}
									/>
								) : listings.length === 1 ? (
									<ChatListingCards
										router={router}
										listings={listings}
									/>
								) : null}
								{message.enquiry_details ? (
									<ChatListingCards
										router={router}
										listings={[message.enquiry_details]}
									/>
								) : null}
								<RowContainer>
									<MessageBody variant="body2">
										{bodyInCard ? null : convertLinksToElements(bodyText, search_query || "")}
									</MessageBody>
									{message.is_edited === true ? (
										<Time
											variant="body2"
											sx={{ marginRight: "0.25rem" }}
										>
											{chatLang.edited}
										</Time>
									) : null}
									<Time variant="body2">{formatMessageTime(message.created_at)}</Time>
									{/* The delivery glyph on your own messages (18 Oct 2026, the app's states): one
									    grey tick sent, two grey delivered to everyone, two orange read by everyone
									    (the server's `is_delivered` / `is_read`, lib/chat/messageState.ts). */}
									{own ? (
										<ReceiptGlyph
											state={message.is_read === true ? "read" : message.is_delivered === true ? "delivered" : "sent"}
										/>
									) : null}
								</RowContainer>
							</React.Fragment>
						)}
					</ColumnContainer>
				</ChatBubble>
				{/* Reactions under the bubble; a click opens the picker to change or take one back. */}
				{!deleted && (message.reaction_summary || []).length > 0 ? (
					<ReactionRow isSent={own}>
						<ReactionPill
							reactions={message.reaction_summary}
							onClick={(event) => (canReactTo(message) ? openPicker(event, message) : undefined)}
						/>
					</ReactionRow>
				) : null}
				{canReactTo(message) ? (
					<ReactTrigger
						className="message-menu-trigger"
						isSent={own}
						size="small"
						aria-label="React"
						onClick={(event) => openPicker(event, message)}
					>
						<AddReactionOutlined sx={{ fontSize: "1.125rem", color: theme.palette.text.secondary }} />
					</ReactTrigger>
				) : null}
				{!deleted && message?._id ? (
					<ReplyTrigger
						className="message-menu-trigger"
						isSent={own}
						size="small"
						aria-label="Reply"
						onClick={() => startReply(message, own)}
					>
						<ReplyOutlined sx={{ fontSize: "1.125rem", color: theme.palette.text.secondary }} />
					</ReplyTrigger>
				) : null}
				<MessageMenuTrigger
					className="message-menu-trigger"
					isSent={own}
					size="small"
					aria-label={chatLang.options}
					onClick={(event) => openOptions(event.currentTarget, message)}
				>
					<KeyboardArrowDown sx={{ fontSize: "1.125rem", color: theme.palette.text.secondary }} />
				</MessageMenuTrigger>
			</ChatBubbleWrapper>
		);
	};

	return (
		<React.Fragment>
			<ThreadFrame>
				<RootDiv ref={threadRef} onScroll={onThreadScroll}>
					{/* Texts still on their way (19 Oct 2026): the clock until the server has stored them, then
					    the stored message takes their place. The thread runs bottom up, so the newest goes first. */}
					{(pending_texts || [])
						.filter((pending) => !messages.some((message: any) => idOf(message._id) === pending.id))
						.slice()
						.reverse()
						.map((pending) => (
							<SentMessage key={"pending-" + pending.id}>
								<ChatBubbleWrapper isSent={true}>
									<ChatBubble isSent={true}>
										<ColumnContainer>
											<RowContainer>
												<MessageBody variant="body2">{convertLinksToElements(pending.body)}</MessageBody>
												<Time variant="body2">{formatMessageTime(pending.created_at)}</Time>
												<ReceiptGlyph state="sending" />
											</RowContainer>
										</ColumnContainer>
									</ChatBubble>
								</ChatBubbleWrapper>
							</SentMessage>
						))}
					<SentMessage>
						{pending_uploads && pending_uploads.length > 0 && selected_files && selected_files.length > 0 && sendFiles && (
							<ImageStackUploadPreview
								session={session}
								dispatch={dispatch}
								profile_context={profile_context}
								router={router}
								realtime_websocket={realtime_websocket}
								receiver_user_id={receiver_user_id}
								chat_id={chat_id}
								images={selected_files}
								pending_uploads={pending_uploads}
								initial_load_done={initial_load_done}
								messages_loading={messages_loading}
								current_message={current_message}
								setCurrentMessage={setCurrentMessage}
								sendFiles={sendFiles}
								setSendFiles={(value) => {
									setSendFiles(value);
									if (!value) {
										setSelectedFilePreview([]);
										setPendingUploads([]);
									}
								}}
								setSelectedFilePreview={setSelectedFilePreview}
								setPendingUploads={setPendingUploads}
								reply_to_message_id={reply_to_message_id || ""}
							/>
						)}
					</SentMessage>

					{/* A broadcast with no chat yet: each pending broadcast from this sender is its own card
					    with I'm interested / Not interested (18 Oct 2026); the newest sits at the bottom. */}
					{Boolean(isBroadcast && broadcast_id) &&
						(requestCards.length > 0 ? requestCards : [{ broadcast_id: String(broadcast_id), invite_status: "pending" }])
							.filter((card) => !reportedBroadcastIds.includes(card.broadcast_id))
							.map(
							(card) => (
								<SentMessage key={"bc-" + card.broadcast_id}>
									<ChatBubbleWrapper isSent={false} sx={{ minWidth: "95%" }}>
										<ChatBubble isSent={false}>
											<ColumnContainer>
												<PropertyCardBroadcast
													session={session}
													dispatch={dispatch}
													profile_context={profile_context}
													router={router}
													broadcast_id={card.broadcast_id}
													source="chat_message"
												/>
												{card.invite_status === "pending" || card.invite_status === "ignored" ? (
													<div style={{ display: "flex", flexDirection: "row", gap: "0.5rem", width: "100%", marginTop: "0.5rem" }}>
														{card.invite_status === "pending" ? (
															<BeegruButton
																variant="outlined"
																flavor="primary"
																loading={answeringCard === card.broadcast_id + ":ignored"}
																disabled={Boolean(answeringCard)}
																onClick={() => answerCard(card.broadcast_id, "ignored")}
																sx={{ flex: 1, textTransform: "none", fontWeight: 600 }}
															>
																{chatLang.not_interested}
															</BeegruButton>
														) : null}
														<BeegruButton
															variant="contained"
															flavor="primary"
															loading={answeringCard === card.broadcast_id + ":accepted"}
															disabled={Boolean(answeringCard)}
															onClick={() => answerCard(card.broadcast_id, "accepted")}
															sx={{ flex: 1, textTransform: "none", fontWeight: 600 }}
														>
															{chatLang.interested}
														</BeegruButton>
													</div>
												) : null}
												{card.invite_status === "ignored" ? (
													<Typography variant="caption" sx={{ color: "text.secondary", marginTop: "0.25rem" }}>
														{chatLang.answered_not_interested}
													</Typography>
												) : null}
												{card.invite_status === "pending" ? (
													<BeegruButton
														variant="text"
														flavor="error"
														size="small"
														disabled={Boolean(answeringCard)}
														onClick={() => setReportBroadcastId(card.broadcast_id)}
														sx={{ alignSelf: "flex-start", textTransform: "none", marginTop: "0.25rem", padding: "0.125rem 0.25rem", minWidth: 0 }}
													>
														{chatLang.report_broadcast}
													</BeegruButton>
												) : null}
											</ColumnContainer>
										</ChatBubble>
									</ChatBubbleWrapper>
								</SentMessage>
							),
						)}
					<Snackbar
						open={answerError}
						autoHideDuration={3000}
						onClose={() => setAnswerError(false)}
						message={chatLang.answer_failed}
					/>


					{messages.reduce((acc: JSX.Element[], message: any, index: number, array: any[]) => {
						const messageDate = new Date(message.created_at);
						const currentDateString = messageDate.toDateString();

						// Check date with next message since we're displaying in reverse
						const nextMessage = array[index + 1];
						const nextDateString = nextMessage ? new Date(nextMessage.created_at).toDateString() : null;

						acc.push(
							// A system line (chat schema 4.7: `message_type: "system"` replaced `system_message`).
							message.message_type === "system" ? (
								/* System Message */

								<SystemMessage key={idOf(message._id) || index}>
									<ChatBubbleSystem>
										<Time variant="body2">
											{formatChatTime(message.created_at)}
										</Time>
									</ChatBubbleSystem>
									<ChatBubbleSystem>
										<MessageBody>{convertLinksToElements(message.body)}</MessageBody>
									</ChatBubbleSystem>
								</SystemMessage>
							) : isOwnMessage(message) ? (
								/* Sent Message */

								<SentMessage
									key={idOf(message._id) || index}
									data-chat-message-id={idOf(message._id)}
									style={searchRowStyle(message)}
								>
									{renderBubble(message, true)}
								</SentMessage>
							) : (
								/* Received Message */

								<ReceivedMessage
									key={idOf(message._id) || index}
									data-chat-message-id={idOf(message._id)}
									style={searchRowStyle(message)}
								>
									{renderBubble(message, false)}
								</ReceivedMessage>
							),
						);

						// "N unread messages" over the oldest message this open read.
						if (unreadDividerId !== "" && idOf(message._id) === unreadDividerId) {
							acc.push(
								<SystemMessage key={`unread-${unreadDividerId}`}>
									<ChatBubbleSystem>
										<MessageBody
											sx={{
												fontSize: "0.6875rem",
												// The read ticks' colour (ChatMessageCards ReceiptGlyph).
												color: "primary.dark",
												textAlign: "center",
												width: "100%",
												fontWeight: 600,
												letterSpacing: "0.04em",
											}}
										>
											{chatLang.unread(Number(unread_count || 0))}
										</MessageBody>
									</ChatBubbleSystem>
								</SystemMessage>,
							);
						}

						// Add date header when date changes or at the end of a group
						if (currentDateString !== nextDateString) {
							acc.push(
								<SystemMessage key={`date-${messageDate.getTime()}`}>
									<ChatBubbleSystem>
										<MessageBody
											sx={{
												fontSize: "0.75rem",
												color: "#637381",
												textAlign: "center",
												width: "100%",
												fontWeight: "500",
											}}
										>
											{getMessageDate(messageDate)}
										</MessageBody>
									</ChatBubbleSystem>
								</SystemMessage>,
							);
						}

						return acc;
					}, [])}

					{/* Load more div for infinite scroll */}

					{messages_count > messages.length && (
						<LoadMoreDiv ref={ref}>
							<LoadingDots aria-label="Loading older messages">
								<span />
								<span />
								<span />
							</LoadingDots>
						</LoadMoreDiv>
					)}
				</RootDiv>

				{/* Go to the newest message (19 Oct 2026, the app's): shows once the reader is up in the history. */}

				<ScrollToNewestButton
					type="button"
					shown={showScrollToNewest}
					tabIndex={showScrollToNewest ? 0 : -1}
					aria-hidden={!showScrollToNewest}
					aria-label={
						newWhileAway > 0
							? `${newWhileAway > 99 ? "99+" : newWhileAway} new messages, go to the newest`
							: "Go to the newest message"
					}
					onClick={scrollToNewest}
				>
					<KeyboardArrowDownRounded sx={{ fontSize: "1.5rem", color: "#6C757D" }} />
					{newWhileAway > 0 ? <NewestCountBadge>{newWhileAway > 99 ? "99+" : newWhileAway}</NewestCountBadge> : null}
				</ScrollToNewestButton>
			</ThreadFrame>

			{/* The reaction picker, from a bubble's hover button or its pill. */}

			<Popover
				open={Boolean(pickerAnchor)}
				anchorEl={pickerAnchor}
				onClose={() => setPickerAnchor(null)}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				transformOrigin={{ vertical: "bottom", horizontal: "center" }}
				disableScrollLock={true}
				slotProps={{ paper: { sx: { borderRadius: "999px", padding: "0.375rem 0.5rem" } } }}
			>
				<ReactionPicker
					current={(pickerTarget?.reaction_summary || []).find((line: any) => line.by_viewer)?.emoji}
					onPick={(emoji) => (pickerTarget ? react(pickerTarget, emoji) : undefined)}
				/>
			</Popover>

			<Snackbar
				open={reactionError !== ""}
				autoHideDuration={3000}
				onClose={() => setReactionError("")}
				message={reactionError}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			/>

			{/* The message options (18 Oct 2026): reactions, Reply, Copy, Edit, Delete. */}

			<Menu
				open={Boolean(optionsAnchor)}
				anchorEl={optionsAnchor}
				onClose={closeOptions}
				disableScrollLock={true}
				slotProps={{ paper: { sx: { borderRadius: "1rem", minWidth: "12rem" } } }}
			>
				{optionsTarget && canReactTo(optionsTarget) ? (
					<div style={{ padding: "0.25rem 0.75rem 0.5rem" }}>
						<ReactionPicker
							current={(optionsTarget.reaction_summary || []).find((line: any) => line.by_viewer)?.emoji}
							onPick={(emoji) => react(optionsTarget, emoji)}
						/>
					</div>
				) : null}
				{optionsTarget && canReactTo(optionsTarget) ? (
					<MenuItem
						onClick={() => {
							closeOptions();
							startReply(optionsTarget, isOwnMessage(optionsTarget));
						}}
					>
						<ListItemIcon>
							<ReplyOutlined fontSize="small" />
						</ListItemIcon>
						<ListItemText>{chatLang.reply}</ListItemText>
					</MenuItem>
				) : null}
				{optionsTarget && canForwardMessage(optionsTarget) && on_forward_selection_change ? (
					<MenuItem
						onClick={() => {
							closeOptions();
							on_forward_selection_change([String(optionsTarget._id)]);
						}}
					>
						<ListItemIcon>
							<ShortcutOutlined fontSize="small" />
						</ListItemIcon>
						<ListItemText>{chatLang.forward}</ListItemText>
					</MenuItem>
				) : null}
				{optionsTarget && copyTextOf(optionsTarget) ? (
					<MenuItem onClick={() => copyMessage(optionsTarget)}>
						<ListItemIcon>
							<ContentCopyOutlined fontSize="small" />
						</ListItemIcon>
						<ListItemText>{chatLang.copy}</ListItemText>
					</MenuItem>
				) : null}
				{optionsTarget && canEdit(optionsTarget) ? (
					<MenuItem onClick={() => startEdit(optionsTarget)}>
						<ListItemIcon>
							<EditOutlined fontSize="small" />
						</ListItemIcon>
						<ListItemText>{chatLang.edit}</ListItemText>
					</MenuItem>
				) : null}
				{optionsTarget && optionsTarget._id && !isOwnMessage(optionsTarget) && !optionsTarget.is_deleted ? (
					<MenuItem
						onClick={() => {
							closeOptions();
							setReportTarget(optionsTarget);
						}}
						sx={{ color: "error.main" }}
					>
						<ListItemIcon sx={{ color: "error.main" }}>
							<FlagOutlined fontSize="small" />
						</ListItemIcon>
						<ListItemText>{chatLang.report}</ListItemText>
					</MenuItem>
				) : null}
				{optionsTarget && optionsTarget._id ? (
					<MenuItem
						onClick={() => {
							closeOptions();
							openDeleteDialog(optionsTarget);
						}}
						sx={{ color: "error.main" }}
					>
						<ListItemIcon sx={{ color: "error.main" }}>
							<DeleteOutline fontSize="small" />
						</ListItemIcon>
						<ListItemText>{chatLang.delete}</ListItemText>
					</MenuItem>
				) : null}
			</Menu>

			{/* Report a message (18 Oct 2026); "Also block" blocks its sender, unless this side already did. */}
			<ChatReportDialog
				open={reportTarget !== null}
				onClose={() => setReportTarget(null)}
				dispatch={dispatch}
				locale={router?.locale}
				profile_context={profile_context}
				chat_id={String(chat_id || "")}
				message_id={reportTarget ? String(reportTarget._id) : ""}
				block_target={
					reportTarget?.sender && !blocked_by_me
						? {
								is_business_profile: reportTarget.sender.is_business_profile === true,
								id: String(
									reportTarget.sender.is_business_profile === true
										? (reportTarget.sender.business_profile_id?._id ?? reportTarget.sender.business_profile_id)
										: (reportTarget.sender.user_id?._id ?? reportTarget.sender.user_id),
								),
							}
						: null
				}
				onDone={(blocked) => {
					setReportTarget(null);
					setReportNotice(blocked ? chatLang.report_and_block_sent : chatLang.report_sent);
				}}
			/>
			<ChatReportDialog
				open={reportBroadcastId !== ""}
				onClose={() => setReportBroadcastId("")}
				dispatch={dispatch}
				locale={router?.locale}
				profile_context={profile_context}
				chat_id=""
				message_id=""
				broadcast_id={reportBroadcastId}
				broadcast_card={true}
				block_target={counterpart_party?.id && !blocked_by_me ? counterpart_party : null}
				onDone={(blocked) => {
					setReportedBroadcastIds((ids) => [...ids, reportBroadcastId]);
					setReportBroadcastId("");
					setReportNotice(blocked ? chatLang.report_and_block_sent : chatLang.broadcast_report_sent);
				}}
			/>
			<Snackbar
				open={reportNotice !== ""}
				autoHideDuration={3000}
				onClose={() => setReportNotice("")}
				message={reportNotice}
			/>

			<Snackbar
				open={copyNotice !== ""}
				autoHideDuration={3000}
				onClose={() => setCopyNotice("")}
				message={copyNotice}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			/>

			{/* The delete dialog: Delete for everyone on your own message within the window, Delete for me on any. */}

			<DeleteDialog
				open={deleteDialogOpen}
				onClose={closeDeleteDialog}
				TransitionProps={{
					onExited: () => {
						setDeleteTarget(null);
						setDeleteError("");
					},
				}}
			>
				<DialogTitle sx={{ padding: "0.75rem 0.5rem 0.25rem" }}>
					<DeleteDialogTitle>{chatLang.delete_title}</DeleteDialogTitle>
				</DialogTitle>
				<DialogContent sx={{ padding: "0rem 0.5rem 1rem" }}>
					<DeleteDialogBody>
						{deleteTarget && canDeleteForEveryone(deleteTarget)
							? chatLang.delete_everyone_body
							: chatLang.delete_me_body}
					</DeleteDialogBody>
					{deleteError ? <DeleteDialogError>{deleteError}</DeleteDialogError> : null}
				</DialogContent>
				<DialogActions
					sx={{
						padding: "0rem 0.5rem 0.75rem",
						gap: "0.5rem",
						flexDirection: deleteTarget && canDeleteForEveryone(deleteTarget) ? "column-reverse" : "row",
						alignItems: "stretch",
						"& > :not(style) ~ :not(style)": { marginLeft: 0 },
					}}
				>
					<BeegruButton
						flavor="primary"
						variant="text"
						onClick={closeDeleteDialog}
						disabled={Boolean(deleting)}
					>
						{chatLang.cancel}
					</BeegruButton>
					<BeegruButton
						flavor="error"
						variant={deleteTarget && canDeleteForEveryone(deleteTarget) ? "outlined" : "contained"}
						onClick={() => confirmDelete("me")}
						disabled={Boolean(deleting)}
						startIcon={
							deleting === "me" ? (
								<CircularProgress
									size={16}
									color="inherit"
								/>
							) : undefined
						}
					>
						{chatLang.delete_for_me}
					</BeegruButton>
					{deleteTarget && canDeleteForEveryone(deleteTarget) ? (
						<BeegruButton
							flavor="error"
							variant="contained"
							onClick={() => confirmDelete("everyone")}
							disabled={Boolean(deleting)}
							startIcon={
								deleting === "everyone" ? (
									<CircularProgress
										size={16}
										color="inherit"
									/>
								) : undefined
							}
						>
							{chatLang.delete_for_everyone}
						</BeegruButton>
					) : null}
				</DialogActions>
			</DeleteDialog>
		</React.Fragment>
	);
};

export default MessageSection;
